import type { ReactNode } from 'react';
import {
  DEAL_GENERATE_ROUTES,
  type DealDetail,
  type DealGenerateRoute,
  type GenerateDealResult,
  type Project,
} from '@forecastify/shared';
import { Badge, Button, Checkbox, Select, Switch, TextInput, cn } from '@forecastify/ui';
import { useT } from '../../i18n';
import { useDuration } from '../../duration';
import { Field } from '../card';
import {
  dealMoney,
  initialsOf,
  lineTypeLabel,
  revenueModeLabel,
  seniorityLabel,
  type DealPerson,
} from './deal-kit';
import {
  lineTarget,
  type ContractType,
  type GenerateDraft,
  type GeneratePreview,
  type GenerateTarget,
} from './generate-plan';

/**
 * ┌───────────────────────────────────────────────────────────────────────────┐
 * │ 20 Oct 2026 · Phase 6 · D8 · the wizard's four bodies, `14-deal-generate-*`. │
 * └───────────────────────────────────────────────────────────────────────────┘
 *
 * Each step takes the draft and a patch callback and owns nothing; `generate-wizard.tsx` holds the
 * state, the step order and the request.
 */

type T = ReturnType<typeof useT>;
type Patch = (patch: Partial<GenerateDraft>) => void;
type Conversion = DealDetail['conversions'][number];

/** The muted box the prototype puts every explanation in. */
function Note({ children, tone = 'muted' }: { children: ReactNode; tone?: 'muted' | 'warn' }) {
  return (
    <div
      className={cn(
        'rounded-lg border p-2.5 text-[11.5px] leading-relaxed',
        tone === 'warn' ? 'border-amber-500/40 bg-amber-500/10' : 'bg-muted/40',
      )}
    >
      {children}
    </div>
  );
}

export function StepIndicator({ step, labels }: { step: number; labels: readonly string[] }) {
  const t = useT();
  return (
    <ol className="flex items-center gap-1.5" aria-label={t('dealGenerate.steps')}>
      {labels.map((label, index) => (
        <li
          key={label}
          className="flex min-w-0 flex-1 items-center gap-1.5"
          aria-current={index === step ? 'step' : undefined}
        >
          <span
            className={cn(
              'grid size-5 shrink-0 place-items-center rounded-full text-[10.5px] font-semibold tabular-nums',
              index === step
                ? 'bg-primary text-primary-foreground'
                : index < step
                  ? 'bg-primary/15 text-primary'
                  : 'bg-muted text-muted-foreground',
            )}
          >
            {index + 1}
          </span>
          <span className="min-w-0 flex-1 truncate text-[11.5px]">{label}</span>
          <span
            aria-hidden="true"
            className={cn('h-px w-4 shrink-0', index < step ? 'bg-primary/40' : 'bg-border')}
          />
        </li>
      ))}
    </ol>
  );
}

/** One conversion as a line: what it made, by name. */
export function ConversionList({ conversions }: { conversions: readonly Conversion[] }) {
  const t = useT();
  return (
    <ul className="flex flex-col gap-0.5">
      {conversions.map((c, index) => (
        <li key={`${c.createdAt}-${String(index)}`} className="flex flex-wrap gap-x-3">
          {c.projectName === null ? null : (
            <span>{t('dealGenerate.converted.project', { name: c.projectName })}</span>
          )}
          {c.contractIdentifier === null ? null : (
            <span>
              {t('dealGenerate.converted.contract', {
                name:
                  c.contractName === null
                    ? c.contractIdentifier
                    : `${c.contractIdentifier} · ${c.contractName}`,
              })}
            </span>
          )}
        </li>
      ))}
    </ul>
  );
}

function routeCopy(t: T, route: DealGenerateRoute): { name: string; when: string; what: string } {
  if (route === 'contract') {
    return {
      name: t('dealGenerate.route.contract.name'),
      when: t('dealGenerate.route.contract.when'),
      what: t('dealGenerate.route.contract.what'),
    };
  }
  if (route === 'project') {
    return {
      name: t('dealGenerate.route.project.name'),
      when: t('dealGenerate.route.project.when'),
      what: t('dealGenerate.route.project.what'),
    };
  }
  return {
    name: t('dealGenerate.route.both.name'),
    when: t('dealGenerate.route.both.when'),
    what: t('dealGenerate.route.both.what'),
  };
}

/* ── 1 · what ─────────────────────────────────────────────────────────────────────────────── */

export function RouteStep({
  deal,
  draft,
  patch,
}: {
  deal: DealDetail;
  draft: GenerateDraft;
  patch: Patch;
}) {
  const t = useT();
  return (
    <div className="flex flex-col gap-2">
      {deal.conversions.length === 0 ? null : (
        <Note>
          <span className="font-medium">{t('dealGenerate.converted.title')}</span>
          <ConversionList conversions={deal.conversions} />
          <span className="text-muted-foreground">{t('dealGenerate.converted.hint')}</span>
        </Note>
      )}
      <div
        role="radiogroup"
        aria-label={t('dealGenerate.step.route')}
        className="flex flex-col gap-2"
      >
        {DEAL_GENERATE_ROUTES.map((route) => {
          const copy = routeCopy(t, route);
          const checked = draft.route === route;
          return (
            <button
              key={route}
              type="button"
              role="radio"
              aria-checked={checked}
              onClick={() => {
                patch({ route });
              }}
              className={cn(
                'flex flex-col gap-1 rounded-lg border p-3 text-left transition-[box-shadow,background-color]',
                checked ? 'border-primary ring-ring/25 ring-[3px]' : 'hover:bg-accent/40',
              )}
            >
              <span className="text-[13px] font-medium">{copy.name}</span>
              <span className="text-muted-foreground text-[11.5px] leading-relaxed">
                {copy.when}
              </span>
              <span className="text-[11.5px] leading-relaxed">{copy.what}</span>
            </button>
          );
        })}
      </div>
      {draft.route !== 'contract' ? null : (
        <div className="mt-1 flex flex-col gap-2 rounded-lg border p-3">
          <Field label={t('dealGenerate.target.label')}>
            <Select
              value={draft.target}
              onChange={(event) => {
                patch({ target: event.target.value as GenerateTarget });
              }}
              options={[
                { value: 'project', label: t('dealGenerate.target.project') },
                { value: 'none', label: t('dealGenerate.target.none') },
              ]}
            />
          </Field>
          {draft.target === 'none' ? (
            <Note tone="warn">{t('dealGenerate.target.noneNote')}</Note>
          ) : null}
        </div>
      )}
    </div>
  );
}

/* ── 2 · project ──────────────────────────────────────────────────────────────────────────── */

export function ProjectStep({
  deal,
  draft,
  patch,
  members,
  preview,
}: {
  deal: DealDetail;
  draft: GenerateDraft;
  patch: Patch;
  members: readonly DealPerson[];
  preview: GeneratePreview;
}) {
  const t = useT();
  return (
    <div className="flex flex-col gap-3">
      <div className="grid gap-3 sm:grid-cols-[minmax(0,1fr)_160px]">
        <Field label={t('dealGenerate.project.name')} required>
          <TextInput
            autoFocus
            value={draft.projectName}
            onChange={(event) => {
              patch({ projectName: event.target.value });
            }}
          />
        </Field>
        <Field label={t('dealGenerate.project.code')}>
          <TextInput
            value={draft.projectCode}
            placeholder={t('dealGenerate.project.codePlaceholder')}
            onChange={(event) => {
              patch({ projectCode: event.target.value.toUpperCase() });
            }}
          />
        </Field>
      </div>
      <div className="grid gap-3 sm:grid-cols-3">
        <div className="flex flex-col gap-1.5">
          <span className="text-[12px] font-medium">{t('dealGenerate.project.client')}</span>
          <div className="bg-muted/40 flex h-8 items-center rounded-md px-2.5 text-[12.5px]">
            <span className="truncate">{deal.clientName}</span>
          </div>
          <span className="text-muted-foreground text-[11.5px]">
            {t('dealGenerate.project.clientLocked')}
          </span>
        </div>
        <Field label={t('dealGenerate.project.pm')} required>
          <Select
            searchable
            searchPlaceholder={t('dealGenerate.searchPerson')}
            value={draft.pmMemberId}
            onChange={(event) => {
              patch({ pmMemberId: event.target.value });
            }}
            options={members.map((member) => ({ value: member.id, label: member.displayName }))}
          />
        </Field>
        <div className="flex flex-col gap-1.5">
          <span className="text-[12px] font-medium">{t('dealGenerate.project.status')}</span>
          <div className="bg-muted/40 flex h-8 items-center rounded-md px-2.5 text-[12.5px]">
            {t('projects.status.active')}
          </div>
          <span className="text-muted-foreground text-[11.5px]">
            {t('dealGenerate.project.statusNote')}
          </span>
        </div>
      </div>
      <Note>
        <span className="text-muted-foreground">
          {preview.startsOn === null && preview.endsOn === null
            ? t('dealGenerate.project.periodNone')
            : t('dealGenerate.project.period', {
                period: `${preview.startsOn ?? '–'} – ${preview.endsOn ?? '–'}`,
              })}
        </span>
      </Note>
    </div>
  );
}

/** The `contract` route's step 2: which of the client's projects the contract joins. */
export function ExistingProjectStep({
  draft,
  patch,
  projects,
}: {
  draft: GenerateDraft;
  patch: Patch;
  projects: readonly Project[] | null;
}) {
  const t = useT();
  if (projects === null) {
    return <div className="text-muted-foreground text-[12px]">{t('common.loading')}</div>;
  }
  if (projects.length === 0) return <Note tone="warn">{t('dealGenerate.existing.empty')}</Note>;
  return (
    <Field label={t('dealGenerate.existing.label')} required note={t('dealGenerate.existing.note')}>
      <Select
        searchable
        searchPlaceholder={t('dealGenerate.existing.pick')}
        value={draft.existingProjectId}
        onChange={(event) => {
          patch({ existingProjectId: event.target.value });
        }}
        options={[
          { value: '', label: t('dealGenerate.existing.pick') },
          ...projects.map((project) => ({ value: project.id, label: project.name })),
        ]}
      />
    </Field>
  );
}

/* ── 3 · contract and line items ──────────────────────────────────────────────────────────── */

function contractTypeLabel(t: T, type: ContractType): string {
  return type === 'milestone' ? t('contract.type.milestone') : t('contract.type.service');
}

export function ContractStep({
  deal,
  draft,
  patch,
  withProject,
  preview,
}: {
  deal: DealDetail;
  draft: GenerateDraft;
  patch: Patch;
  withProject: boolean;
  preview: GeneratePreview;
}) {
  const t = useT();
  const { fmt } = useDuration();
  const toggle = (id: string): void => {
    const next = new Set(draft.lineItemIds);
    if (next.has(id)) next.delete(id);
    else next.add(id);
    patch({ lineItemIds: next });
  };
  const targetText = (line: DealDetail['lineItems'][number]): string => {
    const target = lineTarget(line, draft);
    if (target.kind === 'skipped') return t('dealGenerate.lines.skipped');
    if (target.kind === 'folded') return t('dealGenerate.lines.folded', { name: target.milestone });
    if (target.kind === 'timeAndMaterial') return t('lineItem.type.timeAndMaterial');
    if (target.kind === 'license') return t('lineItem.type.license');
    return t('lineItem.type.fixed');
  };

  return (
    <div className="flex flex-col gap-3">
      <div className="grid gap-3 sm:grid-cols-2">
        <Field
          label={t('dealGenerate.contract.type')}
          note={t('dealGenerate.contract.typeNote', {
            mode: revenueModeLabel(t, deal.revenueMode),
          })}
        >
          <Select
            value={draft.contractType}
            onChange={(event) => {
              patch({ contractType: event.target.value as ContractType });
            }}
            options={(['milestone', 'service'] as const).map((type) => ({
              value: type,
              label: contractTypeLabel(t, type),
            }))}
          />
        </Field>
        <Field label={t('dealGenerate.contract.name')} required>
          <TextInput
            value={draft.contractName}
            onChange={(event) => {
              patch({ contractName: event.target.value });
            }}
          />
        </Field>
      </div>

      {deal.lineItems.length === 0 ? (
        <Note>{t('dealGenerate.lines.empty')}</Note>
      ) : (
        <div className="overflow-hidden rounded-lg border" role="table">
          <div
            role="row"
            className="text-muted-foreground bg-muted/40 flex items-center gap-3 border-b px-3 py-1.5 text-[10.5px] font-medium tracking-wide uppercase"
          >
            <span role="columnheader" className="w-8 shrink-0" />
            <span role="columnheader" className="min-w-0 flex-1">
              {t('dealGenerate.lines.col.line')}
            </span>
            <span role="columnheader" className="w-[104px] shrink-0">
              {t('dealGenerate.lines.col.type')}
            </span>
            <span role="columnheader" className="w-[132px] shrink-0">
              {t('dealGenerate.lines.col.target')}
            </span>
            <span role="columnheader" className="w-[74px] shrink-0 text-right">
              {t('dealGenerate.lines.col.hours')}
            </span>
            <span role="columnheader" className="w-[104px] shrink-0 text-right">
              {t('dealGenerate.lines.col.amount')}
            </span>
          </div>
          {deal.lineItems.map((line) => {
            const on = draft.lineItemIds.has(line.id);
            return (
              <div
                key={line.id}
                role="row"
                className={cn(
                  'flex items-center gap-3 border-b px-3 py-2 last:border-0',
                  !on && 'opacity-50',
                )}
              >
                <span role="cell" className="w-8 shrink-0">
                  <Checkbox
                    checked={on}
                    label={t('dealGenerate.lines.toggle', { name: line.name })}
                    onCheckedChange={() => {
                      toggle(line.id);
                    }}
                  />
                </span>
                <span role="cell" className="flex min-w-0 flex-1 items-center gap-2">
                  <span className="min-w-0 truncate text-[12.5px] font-medium">{line.name}</span>
                  {line.type === 'option' ? (
                    <Badge variant="outline">{t('dealGenerate.lines.option')}</Badge>
                  ) : null}
                </span>
                <span
                  role="cell"
                  className="text-muted-foreground w-[104px] shrink-0 truncate text-[11.5px]"
                >
                  {lineTypeLabel(t, line.type)}
                </span>
                <span role="cell" className="w-[132px] shrink-0 truncate text-[11.5px]">
                  {targetText(line)}
                </span>
                <span
                  role="cell"
                  className="w-[74px] shrink-0 text-right text-[11.5px] tabular-nums"
                >
                  {Number(line.estimateHours) > 0 ? fmt(line.estimateHours) : '–'}
                </span>
                <span
                  role="cell"
                  className="w-[104px] shrink-0 text-right text-[11.5px] tabular-nums"
                >
                  {dealMoney(line.amount, line.currencyCode)}
                </span>
              </div>
            );
          })}
        </div>
      )}

      <div className="flex flex-col gap-2 rounded-lg border p-3">
        <span className="text-[12px] font-medium">
          {t('dealGenerate.milestones.title', { count: deal.milestones.length })}
        </span>
        <div className="flex items-center gap-2.5 text-[12px]">
          <Switch
            checked={draft.milestonesAsLineItems}
            onCheckedChange={(next) => {
              patch({ milestonesAsLineItems: next });
            }}
            label={t('dealGenerate.milestones.asLineItems')}
          />
          <span aria-hidden="true">{t('dealGenerate.milestones.asLineItems')}</span>
        </div>
        <div className="flex items-center gap-2.5 text-[12px]">
          <Switch
            checked={draft.milestonesAsTasks && withProject}
            disabled={!withProject}
            onCheckedChange={(next) => {
              patch({ milestonesAsTasks: next });
            }}
            label={t('dealGenerate.milestones.asTasks')}
          />
          <span aria-hidden="true">{t('dealGenerate.milestones.asTasks')}</span>
        </div>
        {withProject ? null : (
          <span className="text-muted-foreground text-[11.5px]">
            {t('dealGenerate.milestones.tasksNeedProject')}
          </span>
        )}
      </div>

      <Note>
        <span className="tabular-nums">
          {t('dealGenerate.preview', {
            type: contractTypeLabel(t, draft.contractType),
            lines: preview.lineItems,
            revenue: dealMoney(preview.revenue, deal.currencyCode),
            hours: fmt(preview.estimate),
          })}
        </span>
      </Note>
    </div>
  );
}

/* ── 4 · resources and the summary ────────────────────────────────────────────────────────── */

export function ResourcesStep({
  deal,
  draft,
  patch,
  members,
  preview,
  projects,
}: {
  deal: DealDetail;
  draft: GenerateDraft;
  patch: Patch;
  members: readonly DealPerson[];
  preview: GeneratePreview;
  projects: readonly Project[] | null;
}) {
  const t = useT();
  const { fmt } = useDuration();
  const nameOf = (id: string): string =>
    members.find((member) => member.id === id)?.displayName ?? '';
  const matching = draft.route !== 'project' && preview.project !== 'none';

  const projectValue =
    preview.project === 'new'
      ? draft.projectCode.trim() === ''
        ? draft.projectName.trim()
        : `${draft.projectCode.trim()} · ${draft.projectName.trim()}`
      : preview.project === 'existing'
        ? t('dealGenerate.summary.projectExisting', {
            name: projects?.find((p) => p.id === draft.existingProjectId)?.name ?? '',
          })
        : t('dealGenerate.summary.projectNone');

  const rows: { label: string; value: string }[] = [
    { label: t('dealGenerate.summary.project'), value: projectValue },
    ...(preview.contract
      ? [
          {
            label: t('dealGenerate.summary.contract'),
            value: t('dealGenerate.summary.contractValue', {
              type: contractTypeLabel(t, draft.contractType),
              lines: preview.lineItems,
            }),
          },
          {
            label: t('dealGenerate.summary.revenue'),
            value: dealMoney(preview.revenue, deal.currencyCode),
          },
          { label: t('dealGenerate.summary.estimate'), value: fmt(preview.estimate) },
        ]
      : []),
    ...(preview.tasks > 0
      ? [
          {
            label: t('dealGenerate.summary.tasks'),
            value: t('dealGenerate.summary.pieces', { count: preview.tasks }),
          },
        ]
      : []),
    {
      label: t('dealGenerate.summary.allocations'),
      value: t('dealGenerate.summary.people', { count: preview.people }),
    },
  ];

  return (
    <div className="flex flex-col gap-3">
      {draft.route === 'project' ? (
        <Note tone="warn">{t('dealGenerate.match.projectOnly')}</Note>
      ) : !matching ? (
        <Note tone="warn">{t('dealGenerate.match.noProject')}</Note>
      ) : deal.placeholders.length === 0 ? (
        <div className="text-muted-foreground rounded-lg border border-dashed p-4 text-center text-[12px] leading-relaxed">
          {t('dealGenerate.match.empty')}
        </div>
      ) : (
        <>
          {deal.placeholders.map((placeholder) => {
            const pick = draft.matches[placeholder.id] ?? '';
            const meta = [
              placeholder.projectRoleName,
              placeholder.seniority === null ? null : seniorityLabel(t, placeholder.seniority),
              fmt(placeholder.allocatedHours),
              placeholder.headcount > 1
                ? t('dealGenerate.match.headcount', { count: placeholder.headcount })
                : null,
            ].filter((part): part is string => part !== null && part !== '');
            return (
              <div
                key={placeholder.id}
                className="flex flex-wrap items-center gap-3 rounded-lg border p-3"
              >
                <span className="flex min-w-[180px] flex-1 flex-col gap-0.5">
                  <span className="text-[12.5px] font-medium">{placeholder.name}</span>
                  <span className="text-muted-foreground text-[11px] tabular-nums">
                    {meta.join(' · ')}
                  </span>
                </span>
                {pick === '' ? null : (
                  <span
                    aria-hidden="true"
                    className="bg-primary/15 text-primary grid size-6 shrink-0 place-items-center rounded-full text-[9.5px] font-semibold"
                  >
                    {initialsOf(nameOf(pick))}
                  </span>
                )}
                <div className="w-[220px]">
                  <Select
                    searchable
                    searchPlaceholder={t('dealGenerate.searchPerson')}
                    aria-label={t('dealGenerate.match.aria', { name: placeholder.name })}
                    value={pick}
                    onChange={(event) => {
                      patch({
                        matches: { ...draft.matches, [placeholder.id]: event.target.value },
                      });
                    }}
                    options={[
                      { value: '', label: t('dealGenerate.match.pick') },
                      ...members.map((member) => ({
                        value: member.id,
                        label: member.displayName,
                      })),
                    ]}
                  />
                </div>
              </div>
            );
          })}
          <Note>
            <span className="tabular-nums">
              {t('dealGenerate.match.sum', {
                people: preview.people,
                hours: fmt(preview.hours),
              })}
            </span>
          </Note>
        </>
      )}

      <div className="flex flex-col gap-1.5 rounded-lg border p-3">
        <span className="text-[12px] font-medium">{t('dealGenerate.summary.title')}</span>
        <dl className="flex flex-col gap-1.5">
          {rows.map((row) => (
            <div key={row.label} className="flex items-baseline gap-2 text-[12px]">
              <dt className="text-muted-foreground w-[132px] shrink-0">{row.label}</dt>
              <dd className="min-w-0 flex-1 tabular-nums">{row.value}</dd>
            </div>
          ))}
        </dl>
        <span className="text-muted-foreground mt-1 text-[11.5px] leading-relaxed">
          {t('dealGenerate.summary.stepwise')}
        </span>
      </div>
    </div>
  );
}

/* ── the outcome ──────────────────────────────────────────────────────────────────────────── */

export function DoneView({
  result,
  onOpenProject,
  onOpenContract,
}: {
  result: GenerateDealResult;
  onOpenProject: () => void;
  onOpenContract: () => void;
}) {
  const t = useT();
  const rows: { label: string; value: number }[] = [
    { label: t('dealGenerate.done.lineItems'), value: result.lineItems },
    { label: t('dealGenerate.done.tasks'), value: result.tasks },
    { label: t('dealGenerate.done.allocations'), value: result.allocations },
  ];
  return (
    <div className="flex flex-col gap-3" role="status">
      <span className="text-[12.5px]">{t('dealGenerate.done.lead')}</span>
      <div className="flex flex-col gap-2 rounded-lg border p-3 text-[12px]">
        {result.projectId === null ? null : (
          <div className="flex items-center gap-2">
            <span className="text-muted-foreground w-[132px] shrink-0">
              {t('dealGenerate.done.project')}
            </span>
            <Button variant="link" size="sm" onClick={onOpenProject}>
              {t('dealGenerate.done.openProject')}
            </Button>
          </div>
        )}
        {result.contractIdentifier === null ? null : (
          <div className="flex items-center gap-2">
            <span className="text-muted-foreground w-[132px] shrink-0">
              {t('dealGenerate.done.contract')}
            </span>
            <span className="tabular-nums">{result.contractIdentifier}</span>
            <Button variant="link" size="sm" onClick={onOpenContract}>
              {t('dealGenerate.done.openContract')}
            </Button>
          </div>
        )}
        {rows.map((row) => (
          <div key={row.label} className="flex items-baseline gap-2">
            <span className="text-muted-foreground w-[132px] shrink-0">{row.label}</span>
            <span className="tabular-nums">{row.value}</span>
          </div>
        ))}
      </div>
    </div>
  );
}
